import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PostImage from '../components/PostImage';
import { getChallenge, leaveChallenge, deleteChallenge, joinPublicChallenge, reactivateChallenge, updateChallenge, removeChallengeParticipant } from '../lib/api';
import { useInvalidateChallenges } from '../lib/queries';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  ChevronLeft, Users, Calendar, Copy, Check, Trophy,
  Scale, Flame, Crown, LogOut, Globe, Loader2, Trash2, UserPlus, Share2, Pencil, X, UserMinus, RefreshCw
} from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { API_URL } from '../config/api';

export default function ChallengeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useTranslation();
  const invalidateChallenges = useInvalidateChallenges();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('classifica');
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);
  const [removeParticipant, setRemoveParticipant] = useState(null); // { user_id, user_name }
  const [removing, setRemoving] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [reactivating, setReactivating] = useState(false);

  const loadChallenge = useCallback(async () => {
    try {
      const res = await getChallenge(id);
      setChallenge(res.data);
    } catch (err) {
      console.error('Challenge error:', err);
      navigate('/app/challenges');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadChallenge();
  }, [loadChallenge]);

  const copyInviteCode = () => {
    navigator.clipboard.writeText(challenge.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLeave = async () => {
    setLeaving(true);
    try {
      await leaveChallenge(id);
      // Invalidate both 'my' and 'public' challenge lists — the challenge
      // will reappear in the public tab so the user can rejoin if needed.
      invalidateChallenges();
      navigate('/app/challenges');
    } catch (err) {
      console.error('Leave error:', err);
      alert(err.response?.data?.detail || 'Errore');
    } finally {
      setLeaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteChallenge(id);
      setDeleteConfirmText('');
      // Invalidate challenge lists so the deleted one disappears immediately
      // from /app/challenges instead of staying until next route entry.
      invalidateChallenges();
      navigate('/app/challenges');
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.response?.data?.detail || 'Errore durante l\'eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = () => {
    setEditForm({
      name: challenge.name || '',
      description: challenge.description || '',
      start_date: challenge.start_date || '',
      end_date: challenge.end_date || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      alert('Il nome è obbligatorio');
      return;
    }
    if (editForm.end_date && editForm.start_date && editForm.end_date < editForm.start_date) {
      alert('La data di fine deve essere successiva alla data di inizio');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        description: editForm.description || '',
        start_date: editForm.start_date,
        end_date: editForm.end_date
      };
      await updateChallenge(id, payload);
      setShowEditModal(false);
      await loadChallenge();
    } catch (err) {
      console.error('Edit error:', err);
      alert(err.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveParticipant = async () => {
    if (!removeParticipant) return;
    setRemoving(true);
    try {
      await removeChallengeParticipant(id, removeParticipant.user_id);
      setRemoveParticipant(null);
      await loadChallenge();
    } catch (err) {
      console.error('Remove participant error:', err);
      alert(err.response?.data?.detail || 'Errore durante la rimozione');
    } finally {
      setRemoving(false);
    }
  };

  const isCreator = challenge?.creator_id === user?.user_id;
  const isParticipant = challenge?.is_participant === true;
  const [joining, setJoining] = useState(false);
  
  const handleJoin = async () => {
    setJoining(true);
    try {
      await joinPublicChallenge(id);
      // Reload challenge to get updated participant status
      const res = await getChallenge(id);
      setChallenge(res.data);
      invalidateChallenges();
    } catch (err) {
      console.error('Join error:', err);
      alert(err.response?.data?.detail || 'Errore durante la partecipazione');
    } finally {
      setJoining(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const res = await reactivateChallenge(id);
      setChallenge(prev => ({ ...prev, ...res.data }));
      invalidateChallenges();
    } catch (err) {
      console.error('Reactivate error:', err);
      alert(err.response?.data?.detail || 'Errore durante la riattivazione');
    } finally {
      setReactivating(false);
    }
  };

  if (loading) {
    return (
      <div className="page-enter">
        <div className="px-4 py-6">
          <div className="h-8 w-48 skeleton mb-4" />
          <div className="h-32 skeleton rounded-2xl mb-4" />
          <div className="h-64 skeleton rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!challenge) return null;

  const daysLeft = Math.ceil((new Date(challenge.end_date) - new Date()) / (1000 * 60 * 60 * 24));
  const isEnded = daysLeft <= 0;

  // Build share URL and message
  const baseUrl = API_URL.replace('/api', '').replace('https://', '').includes('preview') 
    ? API_URL.replace('/api', '') 
    : 'https://slimmy.app';
  const shareUrl = `${baseUrl}/join/challenge/${challenge.challenge_id}`;
  
  const shareMessages = {
    it: `Unisciti alla sfida "${challenge.name}" su Slimmy! ${challenge.participants?.length || 0} partecipanti stanno già gareggiando.`,
    en: `Join the "${challenge.name}" challenge on Slimmy! ${challenge.participants?.length || 0} participants are already competing.`,
    es: `Únete al reto "${challenge.name}" en Slimmy! ${challenge.participants?.length || 0} participantes ya están compitiendo.`,
    pt: `Entre no desafio "${challenge.name}" no Slimmy! ${challenge.participants?.length || 0} participantes já estão competindo.`,
    de: `Tritt der Challenge "${challenge.name}" bei Slimmy bei! ${challenge.participants?.length || 0} Teilnehmer treten bereits an.`,
    fr: `Rejoins le défi "${challenge.name}" sur Slimmy ! ${challenge.participants?.length || 0} participants sont déjà en compétition.`
  };
  const shareText = shareMessages[language] || shareMessages.it;

  const handleShare = async () => {
    const fullText = `${shareText}\n\n${shareUrl}`;
    
    // Try native share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({ title: challenge.name, text: shareText, url: shareUrl });
        return;
      } catch {}
    }
    
    // Fallback: try WhatsApp deep link
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(fullText)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="h-full flex flex-col" data-testid="challenge-detail-page">
      {/* Fixed Header with Back Button - with safe area */}
      <div 
        className="flex-shrink-0 px-4 pb-3 flex items-center gap-3"
        style={{ 
          backgroundColor: isDark ? '#080D1A' : '#f3f4f6',
          paddingTop: 'max(16px, env(safe-area-inset-top, 16px))'
        }}
      >
        <button 
          onClick={() => navigate('/app/challenges')}
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isDark ? 'bg-white/10' : 'bg-black/10'
          }`}
        >
          <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
        </button>
        <h1 className={`font-display text-lg truncate flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {challenge.name}
        </h1>
        {challenge.is_public && (
          <Globe className="w-5 h-5 text-[#3B82F6] flex-shrink-0" />
        )}
        {isCreator && (
          <button
            onClick={openEditModal}
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              isDark ? 'bg-white/10' : 'bg-black/10'
            }`}
            data-testid="challenge-edit-btn"
            title="Modifica challenge"
          >
            <Pencil className={`w-4 h-4 ${isDark ? 'text-white' : 'text-gray-700'}`} />
          </button>
        )}
        <button
          onClick={handleShare}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-[#E8334A] flex-shrink-0"
          data-testid="challenge-share-btn"
        >
          <Share2 className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-4">
        {/* Hero Header */}
        <div 
          className="relative px-4 pt-4 pb-20"
          style={{ backgroundColor: `${challenge.color}15` }}
        >
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
              style={{ backgroundColor: `${challenge.color}30` }}
            >
              {challenge.emoji}
            </div>
            <div className="flex-1">
              <h2 className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {challenge.name}
              </h2>
              <p className={isDark ? 'text-white/60' : 'text-gray-500'}>
                di {challenge.creator_name}
              </p>
            </div>
          </div>
        </div>

      {/* Stats Cards */}
      <div className="px-4 -mt-10">
        <div className="grid grid-cols-3 gap-3">
          <div className="glass-card p-3 text-center">
            <Users className={`w-5 h-5 mx-auto mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            <span className={`font-display text-xl block ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {challenge.participants?.length || 0}
            </span>
            <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Partecipanti</span>
          </div>
          <div className="glass-card p-3 text-center">
            <Calendar className={`w-5 h-5 mx-auto mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            <span className={`font-display text-xl block ${
              daysLeft <= 3 && !isEnded ? 'text-[#E8334A]' : isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {isEnded ? 'Fine' : `${daysLeft}g`}
            </span>
            <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {isEnded ? 'Conclusa' : 'Rimanenti'}
            </span>
          </div>
          <div className="glass-card p-3 text-center">
            <Scale className={`w-5 h-5 mx-auto mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            <span className={`font-display text-xl block ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {(challenge.participants || []).reduce((sum, p) => sum + (p.weight_lost > 0 ? p.weight_lost : 0), 0).toFixed(1)}
            </span>
            <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Kg persi</span>
          </div>
        </div>
      </div>

      {/* Invite Code */}
      {!isEnded && (
        <div className="px-4 mt-4">
          <div className="glass-card-sm p-4 flex items-center justify-between">
            <div>
              <p className={`text-xs mb-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Codice invito</p>
              <p className={`font-mono text-lg tracking-wider ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {challenge.invite_code}
              </p>
            </div>
            <button 
              onClick={copyInviteCode}
              className="btn-secondary py-2 px-4 text-sm flex items-center gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copiato!' : 'Copia'}
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="px-4 mt-6">
        <div className={`flex gap-2 p-1 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
          <button 
            onClick={() => setActiveTab('classifica')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'classifica' 
                ? isDark ? 'bg-white/10 text-white' : 'bg-white text-gray-900 shadow-sm'
                : isDark ? 'text-white/60' : 'text-gray-500'
            }`}
          >
            Classifica
          </button>
          <button 
            onClick={() => setActiveTab('post')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'post' 
                ? isDark ? 'bg-white/10 text-white' : 'bg-white text-gray-900 shadow-sm'
                : isDark ? 'text-white/60' : 'text-gray-500'
            }`}
          >
            Post
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 mt-4">
        {activeTab === 'classifica' && (
          <div className="space-y-2">
            {challenge.participants?.map((p, index) => {
              // Calculate progress towards goal
              const hasGoal = p.goal_weight && p.start_weight && p.start_weight > p.goal_weight;
              const weightToLose = hasGoal ? p.start_weight - p.goal_weight : 0;
              const progressPercent = hasGoal && weightToLose > 0 
                ? Math.min(100, Math.max(0, (p.weight_lost / weightToLose) * 100)) 
                : (p.weight_lost > 0 ? Math.min(100, p.weight_lost * 10) : 0); // Fallback: show some progress based on kg lost
              
              return (
              <div 
                key={p.user_id}
                className={`glass-card-sm p-4 ${
                  p.user_id === user?.user_id ? 'ring-1 ring-[#E8334A]/50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-display font-bold flex-shrink-0 ${
                    index === 0 ? 'bg-[#F59E0B] text-black' :
                    index === 1 ? 'bg-[#94A3B8] text-black' :
                    index === 2 ? 'bg-[#CD7F32] text-black' :
                    isDark ? 'bg-white/10 text-white/60' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {index === 0 ? <Crown className="w-4 h-4" /> : index + 1}
                  </div>

                  {p.user_picture ? (
                    <img src={p.user_picture} alt="" className="w-10 h-10 avatar flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 avatar-placeholder text-sm flex-shrink-0">
                      {p.user_name?.[0]?.toUpperCase()}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {p.user_name}
                      {p.user_id === user?.user_id && (
                        <span className={`text-xs ml-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>(tu)</span>
                      )}
                    </p>
                    <div className={`flex items-center gap-3 text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                      {p.streak_days > 0 && (
                        <span className="flex items-center gap-1">
                          <Flame className="w-3 h-3 text-[#F59E0B]" />
                          {p.streak_days}
                        </span>
                      )}
                      {p.current_weight && (
                        <span className="flex items-center gap-1">
                          <Scale className="w-3 h-3" />
                          {p.current_weight} kg
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <span className={`font-display text-lg ${
                      p.weight_lost > 0 ? 'text-[#16A34A]' : isDark ? 'text-white/40' : 'text-gray-400'
                    }`}>
                      {p.weight_lost > 0 ? `-${p.weight_lost}` : '0'}
                    </span>
                    <span className={`text-xs block ${isDark ? 'text-white/40' : 'text-gray-400'}`}>kg persi</span>
                  </div>

                  {isCreator && p.user_id !== challenge.creator_id && (
                    <button
                      onClick={() => setRemoveParticipant({ user_id: p.user_id, user_name: p.user_name })}
                      className={`ml-2 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100'
                      } transition-colors`}
                      title={`Rimuovi ${p.user_name}`}
                      data-testid={`challenge-remove-participant-${p.user_id}`}
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* Progress Bar */}
                <div className="mt-3">
                  <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        progressPercent >= 100 ? 'bg-[#16A34A]' : 
                        progressPercent >= 50 ? 'bg-[#3B82F6]' : 
                        'bg-[#F59E0B]'
                      }`}
                      style={{ width: `${Math.round(progressPercent)}%` }}
                    />
                  </div>
                  <div className={`flex justify-between items-center mt-1 text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                    <span>{Math.round(progressPercent)}% {hasGoal ? 'obiettivo' : 'progresso'}</span>
                    {hasGoal && (
                      <span>Goal: {p.goal_weight} kg</span>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {activeTab === 'post' && (
          <div className="space-y-3">
            {challenge.posts?.length === 0 ? (
              <div className="text-center py-12">
                <p className={isDark ? 'text-white/60' : 'text-gray-500'}>Nessun post ancora</p>
              </div>
            ) : (
              challenge.posts?.map(post => (
                <div key={post.post_id} className="glass-card-sm p-4">
                  <div className="flex items-center gap-3 mb-3">
                    {post.user_picture ? (
                      <img src={post.user_picture} alt="" className="w-8 h-8 avatar" />
                    ) : (
                      <div className="w-8 h-8 avatar-placeholder text-xs">
                        {post.user_name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {post.user_name}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        {formatTime(post.created_at)}
                      </p>
                    </div>
                  </div>
                  {post.text && (
                    <p className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{post.text}</p>
                  )}
                  {(post.image_url || (Array.isArray(post.image_urls) && post.image_urls.length > 0)) && (
                    <div className="mt-3">
                      <PostImage post={post} maxHeightClass="max-h-64" />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Join Challenge Button - only for non-participants on public challenges */}
      {!isParticipant && challenge.is_public && !isEnded && (
        <div className="px-4 mt-6">
          <button
            onClick={handleJoin}
            disabled={joining}
            className={`w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 ${
              isDark ? 'bg-[#3B82F6] text-white hover:bg-[#2563EB]' : 'bg-[#3B82F6] text-white hover:bg-[#2563EB]'
            } transition-colors disabled:opacity-50`}
          >
            {joining ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4" />
            )}
            {joining ? 'Partecipazione...' : 'Partecipa alla challenge'}
          </button>
        </div>
      )}

      {/* Leave Challenge Button - only for participants (non-creators) */}
      {isParticipant && !isCreator && !isEnded && (
        <div className="px-4 mt-6">
          <button
            onClick={() => setShowLeaveModal(true)}
            className={`w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 ${
              isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100'
            } transition-colors`}
          >
            <LogOut className="w-4 h-4" />
            Abbandona challenge
          </button>
        </div>
      )}

      {/* Re-enter button — former participants of an ended challenge */}
      {!isParticipant && isEnded && challenge.was_participant && (
        <div className="px-4 mt-6">
          <button
            data-testid="reenter-challenge-btn"
            onClick={handleJoin}
            disabled={joining}
            className="w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors disabled:opacity-50"
          >
            {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {t('challenges.reenter')}
          </button>
        </div>
      )}

      {/* Reactivate button — creator of an ended challenge */}
      {isCreator && isEnded && (
        <div className="px-4 mt-6">
          <button
            data-testid="reactivate-challenge-btn"
            onClick={handleReactivate}
            disabled={reactivating}
            className="w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors disabled:opacity-50"
          >
            {reactivating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {t('challenges.reactivate')}
          </button>
        </div>
      )}

      {/* Delete Challenge Button - only for creator */}
      {isCreator && (
        <div className="px-4 mt-6">
          <button
            onClick={() => setShowDeleteModal(true)}
            className={`w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 ${
              isDark ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' : 'bg-red-50 text-red-600 hover:bg-red-100'
            } transition-colors`}
          >
            <Trash2 className="w-4 h-4" />
            Elimina challenge
          </button>
        </div>
      )}
      </div>{/* End scrollable content */}

      {/* Leave Confirmation Modal */}
      {showLeaveModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowLeaveModal(false)}
        >
          <div 
            className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <LogOut className="w-7 h-7 text-red-500" />
            </div>
            <h3 className={`font-display text-lg mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Abbandonare la challenge?
            </h3>
            <p className={`text-sm text-center mb-6 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              Uscirai da "{challenge.name}" e non vedrai più i post dei partecipanti nel feed.
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowLeaveModal(false)} 
                className="btn-secondary flex-1"
              >
                Annulla
              </button>
              <button 
                onClick={handleLeave}
                disabled={leaving}
                className="flex-1 py-3 px-4 rounded-full bg-red-500 text-white font-semibold flex items-center justify-center gap-2"
              >
                {leaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Abbandona'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Challenge Modal */}
      {showEditModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowEditModal(false)}
        >
          <div 
            className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Modifica challenge
              </h3>
              <button 
                onClick={() => setShowEditModal(false)}
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isDark ? 'bg-white/10 text-white' : 'bg-black/5 text-gray-600'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Nome</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className={`w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A] ${
                    isDark ? 'bg-white/5 text-white placeholder-white/30' : 'bg-gray-100 text-gray-900'
                  }`}
                  data-testid="challenge-edit-name-input"
                />
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Descrizione (opzionale)</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className={`w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A] resize-none ${
                    isDark ? 'bg-white/5 text-white placeholder-white/30' : 'bg-gray-100 text-gray-900'
                  }`}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Inizio</label>
                  <input
                    type="date"
                    value={editForm.start_date}
                    onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                    className={`w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A] ${
                      isDark ? 'bg-white/5 text-white' : 'bg-gray-100 text-gray-900'
                    }`}
                    data-testid="challenge-edit-start-date"
                  />
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Fine</label>
                  <input
                    type="date"
                    value={editForm.end_date}
                    onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                    className={`w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A] ${
                      isDark ? 'bg-white/5 text-white' : 'bg-gray-100 text-gray-900'
                    }`}
                    data-testid="challenge-edit-end-date"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button 
                onClick={() => setShowEditModal(false)} 
                className="btn-secondary flex-1"
              >
                Annulla
              </button>
              <button 
                onClick={handleSaveEdit}
                disabled={saving || !editForm.name.trim()}
                className="flex-1 py-3 px-4 rounded-full bg-[#E8334A] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="challenge-edit-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Participant Confirmation Modal */}
      {removeParticipant && (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setRemoveParticipant(null)}
        >
          <div 
            className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <UserMinus className="w-7 h-7 text-red-500" />
            </div>
            <h3 className={`font-display text-lg mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Rimuovere il partecipante?
            </h3>
            <p className={`text-sm text-center mb-6 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              <strong>{removeParticipant.user_name}</strong> verrà rimosso dalla challenge e non potrà più vedere né pubblicare post qui.
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setRemoveParticipant(null)} 
                className="btn-secondary flex-1"
              >
                Annulla
              </button>
              <button 
                onClick={handleRemoveParticipant}
                disabled={removing}
                className="flex-1 py-3 px-4 rounded-full bg-red-500 text-white font-semibold flex items-center justify-center gap-2"
                data-testid="challenge-remove-participant-confirm-btn"
              >
                {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Rimuovi'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Delete Confirmation Modal */}
      {showDeleteModal && (() => {
        const participantCount = challenge.participants?.length || 0;
        const hasOthers = participantCount > 1;
        const keyword = t('challenges.deleteConfirm.keyword');
        const isMatch = deleteConfirmText.trim().toUpperCase() === keyword.toUpperCase();
        const canDelete = !hasOthers || isMatch;
        return (
        <div 
          className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          style={{ zIndex: 9999 }}
          onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
        >
          <div 
            className={`w-full max-w-sm rounded-2xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-7 h-7 text-red-500" />
            </div>
            <h3 className={`font-display text-lg mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('challenges.deleteConfirm.title')}
            </h3>
            <p className={`text-sm text-center mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {hasOthers
                ? t('challenges.deleteConfirm.warningWithParticipants', { count: participantCount })
                : `Questa azione è irreversibile. Tutti i post e i dati di "${challenge.name}" saranno eliminati definitivamente.`
              }
            </p>
            {hasOthers && (
              <div className="mb-5">
                <p className={`text-xs mb-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  {t('challenges.deleteConfirm.instructions', { keyword })}
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder={t('challenges.deleteConfirm.placeholder', { keyword })}
                  data-testid="challenge-delete-confirm-input"
                  className={`w-full px-3 py-2.5 rounded-lg text-sm font-mono tracking-wider outline-none border-2 transition-colors ${
                    isMatch
                      ? 'border-red-500'
                      : isDark
                        ? 'border-white/10 bg-white/5 text-white'
                        : 'border-gray-200 bg-gray-50 text-gray-900'
                  }`}
                  autoCapitalize="characters"
                  autoComplete="off"
                />
              </div>
            )}
            <div className="flex gap-3">
              <button 
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
                className="btn-secondary flex-1"
                data-testid="challenge-delete-cancel-btn"
              >
                {t('challenges.deleteConfirm.cancel')}
              </button>
              <button 
                onClick={handleDelete}
                disabled={deleting || !canDelete}
                data-testid="challenge-delete-confirm-btn"
                className={`flex-1 py-3 px-4 rounded-full font-semibold flex items-center justify-center gap-2 transition-opacity ${
                  canDelete
                    ? 'bg-red-500 text-white'
                    : 'bg-red-500/40 text-white/70 cursor-not-allowed'
                }`}
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('challenges.deleteConfirm.delete')}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

function formatTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (hours < 24) return `${hours}h fa`;
  if (days < 7) return `${days}g fa`;
  
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}
